// ===========================================
// Payment Reminder steps card (Invoice settings) — #372 slice B (B5.1)
// ===========================================
// md/plan/372.md §4 B5. The ladder is a LIST of steps (kind, name, day offset, optional repeat),
// each with its own enabled switch. It replaces the four fixed rows of PaymentRemindersCard. The
// server is the authority on every rule (ranges, "two enabled steps more than a day apart", the
// one Last Chance step, the fees pin); the checks here only save a round trip, and every 4xx is
// shown in the server's own words by the hooks' snackbar. A step's WORDING is edited in System
// Emails, through `TemplateEditorDialog` with the template key `step:<id>`.
//
// The billing contact email stays a flat invoice setting, so it stays in the invoice form.

import { useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Email as EmailIcon } from '@mui/icons-material';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  adminSettingsKeys,
  useCreateReminderStep,
  useDeleteReminderStep,
  useReminderSteps,
  useUpdateReminderStep,
} from '../../../hooks/useAdminSettings';
import { useQueryClient } from '@tanstack/react-query';
import { getEmailTemplates } from '../../../api/emailBranding.api';
import {
  REMINDER_MAX_LIVE_STEPS,
  REMINDER_MAX_REPEATS_RANGE,
  REMINDER_REPEAT_EVERY_RANGE,
  REMINDER_STEP_DAY_RANGE,
  type ReminderStep,
  type ReminderStepKind,
} from '../../../types/adminSettings.types';
import TemplateEditorDialog from './TemplateEditorDialog';
import type { InvoiceSettingsFormData } from './InvoiceSettingsTab';

/** Loose on purpose: the server validates properly; this only catches an obvious typo. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const KIND_LABEL: Record<ReminderStepKind, string> = {
  BEFORE_DUE: 'Before due date',
  ON_DUE: 'On due date',
  LAST_CHANCE: 'Last chance',
  OVERDUE: 'Overdue',
};

const KIND_HELP: Record<ReminderStepKind, string> = {
  BEFORE_DUE: 'A heads-up before the invoice is due.',
  ON_DUE: 'Goes on the due date itself. Only one.',
  LAST_CHANCE: 'The final notice before late fees. Only one.',
  OVERDUE: 'After the due date. Can repeat until the invoice is paid.',
};

const DEFAULT_NAME: Record<ReminderStepKind, string> = {
  BEFORE_DUE: 'Due soon reminder',
  ON_DUE: 'Due today reminder',
  LAST_CHANCE: 'Final notice',
  OVERDUE: 'Overdue reminder',
};

const DEFAULT_DAYS: Record<ReminderStepKind, number> = {
  BEFORE_DUE: 3,
  ON_DUE: 0,
  LAST_CHANCE: 14,
  OVERDUE: 1,
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "3 days before the due date", "On the due date", "7 days after the due date, then every 7 days (max 4)". */
export function describeStepTiming(step: Pick<ReminderStep, 'kind' | 'dayOffset' | 'repeatEveryDays' | 'maxRepeats'>): string {
  let text: string;
  if (step.dayOffset === 0) text = 'On the due date';
  else if (step.dayOffset < 0) text = `${plural(-step.dayOffset, 'day')} before the due date`;
  else text = `${plural(step.dayOffset, 'day')} after the due date`;
  if (step.kind === 'OVERDUE' && step.repeatEveryDays) {
    text += `, then every ${plural(step.repeatEveryDays, 'day')}`;
    text += step.maxRepeats ? ` (max ${step.maxRepeats})` : ', until paid';
  }
  return text;
}

// ===========================================
// ADD / EDIT DIALOG
// ===========================================

interface StepDialogProps {
  /** null = adding. */
  step: ReminderStep | null;
  existingKinds: ReminderStepKind[];
  lateFeesOn: boolean;
  lateFeeFirstDays: number;
  saving: boolean;
  onClose: () => void;
  onSubmit: (value: {
    kind: ReminderStepKind;
    name: string;
    dayOffset: number;
    repeatEveryDays: number | null;
    maxRepeats: number | null;
  }) => void;
}

function StepDialog({ step, existingKinds, lateFeesOn, lateFeeFirstDays, saving, onClose, onSubmit }: StepDialogProps) {
  const adding = step === null;
  // A kind that already exists once is not offered again (the server would refuse it).
  const takenOnce = (k: ReminderStepKind) => (k === 'LAST_CHANCE' || k === 'ON_DUE') && existingKinds.includes(k);
  const firstFreeKind = (['BEFORE_DUE', 'OVERDUE', 'ON_DUE', 'LAST_CHANCE'] as ReminderStepKind[]).find(
    (k) => !takenOnce(k)
  ) as ReminderStepKind;
  const initialKind = step?.kind ?? firstFreeKind;

  const [kind, setKind] = useState<ReminderStepKind>(initialKind);
  const [name, setName] = useState(step?.name ?? DEFAULT_NAME[initialKind]);
  const [nameTouched, setNameTouched] = useState(!adding);
  // The days box is a plain positive number; BEFORE_DUE sends it negated.
  const [days, setDays] = useState<string>(String(step ? Math.abs(step.dayOffset) : DEFAULT_DAYS[initialKind]));
  const [repeat, setRepeat] = useState(!!step?.repeatEveryDays);
  const [every, setEvery] = useState<string>(String(step?.repeatEveryDays ?? 7));
  const [max, setMax] = useState<string>(step?.maxRepeats ? String(step.maxRepeats) : '');
  const [submitted, setSubmitted] = useState(false);

  const range = REMINDER_STEP_DAY_RANGE[kind];
  // BEFORE_DUE is stored negative (-60..-1) but typed as a plain "days before" number (1..60).
  const dayMin = kind === 'BEFORE_DUE' ? Math.abs(range.max) : range.min;
  // With late fees on, the final notice must go out BEFORE the first late fee (day N), so N - 1 at most.
  const beforeLateFees = kind === 'LAST_CHANCE' && lateFeesOn;
  const dayMax = kind === 'BEFORE_DUE' ? Math.abs(range.min) : beforeLateFees ? Math.min(range.max, lateFeeFirstDays - 1) : range.max;
  const daysNum = Number(days);
  const everyNum = Number(every);
  const maxNum = Number(max);

  const errors: { name?: string; days?: string; every?: string; max?: string } = {};
  if (!name.trim()) errors.name = 'Give the step a name';
  else if (name.trim().length > 100) errors.name = 'At most 100 characters';
  if (kind !== 'ON_DUE' && (!Number.isInteger(daysNum) || daysNum < dayMin || daysNum > dayMax)) {
    errors.days = beforeLateFees && daysNum > dayMax
      ? `Must go out before the first late fee on day ${lateFeeFirstDays}: enter ${dayMax} or fewer`
      : `Enter a whole number from ${dayMin} to ${dayMax}`;
  }
  if (kind === 'OVERDUE' && repeat) {
    if (!Number.isInteger(everyNum) || everyNum < REMINDER_REPEAT_EVERY_RANGE.min || everyNum > REMINDER_REPEAT_EVERY_RANGE.max) {
      errors.every = `Enter a whole number from ${REMINDER_REPEAT_EVERY_RANGE.min} to ${REMINDER_REPEAT_EVERY_RANGE.max}`;
    }
    if (max.trim() !== '' && (!Number.isInteger(maxNum) || maxNum < REMINDER_MAX_REPEATS_RANGE.min || maxNum > REMINDER_MAX_REPEATS_RANGE.max)) {
      errors.max = `Leave blank for until paid, or enter ${REMINDER_MAX_REPEATS_RANGE.min} to ${REMINDER_MAX_REPEATS_RANGE.max}`;
    }
  }
  const hasErrors = Object.keys(errors).length > 0;

  const changeKind = (next: ReminderStepKind) => {
    setKind(next);
    if (!nameTouched) setName(DEFAULT_NAME[next]);
    setDays(String(DEFAULT_DAYS[next]));
    if (next !== 'OVERDUE') setRepeat(false);
  };

  const submit = () => {
    setSubmitted(true);
    if (hasErrors) return;
    onSubmit({
      kind,
      name: name.trim(),
      dayOffset: kind === 'ON_DUE' ? 0 : kind === 'BEFORE_DUE' ? -daysNum : daysNum,
      repeatEveryDays: kind === 'OVERDUE' && repeat ? everyNum : null,
      maxRepeats: kind === 'OVERDUE' && repeat && max.trim() !== '' ? maxNum : null,
    });
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{adding ? 'Add reminder step' : 'Edit reminder step'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1, minWidth: 0 }}>
          {adding ? (
            <TextField
              select
              label="Kind"
              value={kind}
              onChange={(e) => changeKind(e.target.value as ReminderStepKind)}
              helperText={KIND_HELP[kind]}
              fullWidth
            >
              {(Object.keys(KIND_LABEL) as ReminderStepKind[]).map((k) => (
                <MenuItem key={k} value={k} disabled={takenOnce(k)}>
                  {KIND_LABEL[k]}
                  {takenOnce(k) ? ' (already added)' : ''}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {KIND_LABEL[kind]}: {KIND_HELP[kind]}
            </Typography>
          )}

          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameTouched(true);
            }}
            error={submitted && !!errors.name}
            helperText={(submitted && errors.name) || 'Shown to you in the history and System Emails; families never see it.'}
            inputProps={{ maxLength: 100 }}
            fullWidth
          />

          {kind !== 'ON_DUE' && (
            <TextField
              label={kind === 'BEFORE_DUE' ? 'Days before the due date' : 'Days after the due date'}
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              error={submitted && !!errors.days}
              helperText={
                (submitted && errors.days) ||
                (kind === 'LAST_CHANCE' && lateFeesOn
                  ? `Late fees start ${plural(lateFeeFirstDays, 'day')} after the due date, so this must go out before then.`
                  : undefined)
              }
              InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
              inputProps={{ min: dayMin, max: dayMax }}
              sx={{ maxWidth: 240 }}
            />
          )}

          {kind === 'OVERDUE' && (
            <Stack spacing={1.5}>
              <FormControlLabel
                control={<Switch checked={repeat} onChange={(e) => setRepeat(e.target.checked)} />}
                label="Repeat while the invoice is still owing"
              />
              {repeat && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Repeat every"
                    type="number"
                    value={every}
                    onChange={(e) => setEvery(e.target.value)}
                    error={submitted && !!errors.every}
                    helperText={submitted && errors.every}
                    InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                    inputProps={{ min: REMINDER_REPEAT_EVERY_RANGE.min, max: REMINDER_REPEAT_EVERY_RANGE.max }}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  />
                  <TextField
                    label="Stop after"
                    type="number"
                    value={max}
                    onChange={(e) => setMax(e.target.value)}
                    error={submitted && !!errors.max}
                    helperText={(submitted && errors.max) || 'Blank = keep going until paid'}
                    InputProps={{ endAdornment: <InputAdornment position="end">repeats</InputAdornment> }}
                    inputProps={{ min: REMINDER_MAX_REPEATS_RANGE.min, max: REMINDER_MAX_REPEATS_RANGE.max }}
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : adding ? 'Add step' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

interface ReminderStepsCardProps {
  /** The invoice form: the billing contact email stays a flat invoice setting. */
  control: Control<InvoiceSettingsFormData>;
}

export default function ReminderStepsCard({ control }: ReminderStepsCardProps) {
  const { can } = usePermissions();
  const canWrite = can('operations_invoices', 'write') && can('system_settings', 'write');
  // A step's wording is edited through the System Emails routes (communications_settings).
  const canEditWording = canWrite && can('communications_settings', 'write');
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useReminderSteps();
  const createStep = useCreateReminderStep();
  const updateStep = useUpdateReminderStep();
  const deleteStep = useDeleteReminderStep();
  const busy = createStep.isPending || updateStep.isPending || deleteStep.isPending;

  const [dialog, setDialog] = useState<{ step: ReminderStep | null } | null>(null);
  const [toDelete, setToDelete] = useState<ReminderStep | null>(null);
  /** The step whose wording is open (or loading) in the template editor. */
  const [wordingStepId, setWordingStepId] = useState<string | null>(null);

  // The same list System Emails reads (shared cache). Fetched only once someone asks for wording.
  const templates = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: getEmailTemplates,
    enabled: wordingStepId !== null,
  });
  const wordingKey = wordingStepId ? `step:${wordingStepId}` : null;
  const wordingTemplate =
    wordingKey && Array.isArray(templates.data) ? templates.data.find((t) => t.key === wordingKey) : undefined;

  const steps = data?.steps ?? [];
  const lateFeesOn = data?.lateFeesOn ?? false;
  const atLimit = steps.length >= REMINDER_MAX_LIVE_STEPS;

  const submitDialog = (value: Parameters<StepDialogProps['onSubmit']>[0]) => {
    if (!dialog) return;
    const done = { onSuccess: () => setDialog(null) };
    if (dialog.step) {
      const { kind: _kind, ...fields } = value;
      void _kind;
      updateStep.mutate({ stepId: dialog.step.id, input: fields }, done);
    } else {
      createStep.mutate(value, done);
    }
  };

  const lastChanceLockedText = 'Late fees are on and need this step: they start after the final notice. Turn late fees off first.';

  return (
    <Card variant="outlined">
      <CardHeader
        title="Payment Reminders"
        subheader="Emails that remind families about unpaid invoices, in the order they go"
        action={
          canWrite ? (
            <Tooltip title={atLimit ? `A school can have up to ${REMINDER_MAX_LIVE_STEPS} reminder steps.` : ''}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setDialog({ step: null })}
                  disabled={!data || atLimit || busy}
                >
                  Add step
                </Button>
              </span>
            </Tooltip>
          ) : undefined
        }
      />
      <CardContent>
        <Stack spacing={2.5}>
          {data && !data.remindersActive && (
            <Alert severity="info">
              Automatic reminders aren&apos;t switched on yet. You can still send one by hand from an invoice.
            </Alert>
          )}

          {isLoading ? (
            <Box>
              <Skeleton variant="rectangular" height={64} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={64} />
            </Box>
          ) : isError || !data ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  Try again
                </Button>
              }
            >
              Could not load the reminder steps.
            </Alert>
          ) : steps.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No reminder steps are set up, so families are not sent payment reminders.
              {canWrite ? ' Use Add step to create one.' : ''}
            </Typography>
          ) : (
            <Stack spacing={1.5} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {steps.map((step) => {
                const pinned = step.kind === 'LAST_CHANCE' && lateFeesOn;
                const anyCustom = step.customised.subject || step.customised.preheader || step.customised.body;
                return (
                  <Box
                    component="li"
                    key={step.id}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, minWidth: 0 }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip size="small" color="primary" variant="outlined" label={KIND_LABEL[step.kind]} />
                          <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                            {step.name}
                          </Typography>
                          {anyCustom && <Chip size="small" label="Wording customised" />}
                        </Stack>
                        <Typography variant="body2" color={step.enabled ? 'text.primary' : 'text.disabled'} sx={{ mt: 0.5 }}>
                          {describeStepTiming(step)}
                          {!step.enabled ? ' (off)' : ''}
                        </Typography>
                      </Box>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} alignItems={{ sm: 'center' }}>
                        <Tooltip title={pinned ? lastChanceLockedText : ''}>
                          <span>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={step.enabled}
                                  disabled={!canWrite || busy || (pinned && step.enabled)}
                                  onChange={(e) =>
                                    updateStep.mutate({ stepId: step.id, input: { enabled: e.target.checked } })
                                  }
                                  inputProps={{ 'aria-label': `${step.name} enabled` }}
                                />
                              }
                              label="On"
                            />
                          </span>
                        </Tooltip>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={step.copyBillingContact}
                              disabled={!canWrite || busy}
                              onChange={(e) =>
                                updateStep.mutate({ stepId: step.id, input: { copyBillingContact: e.target.checked } })
                              }
                              inputProps={{ 'aria-label': `${step.name} copy to billing contact` }}
                            />
                          }
                          label="Copy billing contact"
                        />
                      </Stack>

                      {canWrite && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {canEditWording && (
                            <Button
                              size="small"
                              startIcon={<EmailIcon />}
                              onClick={() => setWordingStepId(step.id)}
                              aria-label={`Edit wording of ${step.name}`}
                            >
                              Edit wording
                            </Button>
                          )}
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => setDialog({ step })}
                            disabled={busy}
                            aria-label={`Edit ${step.name}`}
                          >
                            Edit
                          </Button>
                          <Tooltip title={pinned ? lastChanceLockedText : ''}>
                            <span>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => setToDelete(step)}
                                disabled={busy || pinned}
                                aria-label={`Delete ${step.name}`}
                              >
                                Delete
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}

          {wordingStepId && templates.isLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading the email wording…</Typography>
            </Stack>
          )}
          {wordingStepId && templates.isError && (
            <Alert
              severity="error"
              onClose={() => setWordingStepId(null)}
              action={
                <Button color="inherit" size="small" onClick={() => templates.refetch()}>
                  Try again
                </Button>
              }
            >
              Could not load the email wording.
            </Alert>
          )}
          {wordingStepId && templates.isSuccess && !wordingTemplate && (
            <Alert severity="warning" onClose={() => setWordingStepId(null)}>
              This step&apos;s email wording isn&apos;t available yet. Reload the page and try again.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Reminders go to every contact on the family, at about 9am school time, while an invoice is still owing,
            and stop once it is paid. Each step&apos;s wording is edited here or in System Emails.
          </Typography>

          <Divider />

          <Controller
            name="billingContactEmail"
            control={control}
            rules={{
              validate: (v) => !v.trim() || EMAIL_PATTERN.test(v.trim()) || 'Enter a valid email address',
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="email"
                label="Billing contact email"
                placeholder="accounts@yourschool.com"
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ||
                  'Where families are told to ask about a payment plan. Invoice emails mention it only once this is set; reminders use your school email when it is blank.'
                }
                inputProps={{ maxLength: 320 }}
                sx={{ maxWidth: 420 }}
                fullWidth
              />
            )}
          />
        </Stack>
      </CardContent>

      {dialog && (
        <StepDialog
          step={dialog.step}
          existingKinds={steps.map((s) => s.kind)}
          lateFeesOn={lateFeesOn}
          lateFeeFirstDays={data?.lateFeeFirstDays ?? 7}
          saving={createStep.isPending || updateStep.isPending}
          onClose={() => setDialog(null)}
          onSubmit={submitDialog}
        />
      )}

      <Dialog open={!!toDelete} onClose={() => setToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this reminder step?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {toDelete?.name} will stop being sent. Reminders it has already sent stay in each invoice&apos;s history.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteStep.isPending}
            onClick={() => {
              if (!toDelete) return;
              deleteStep.mutate(toDelete.id, { onSettled: () => setToDelete(null) });
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <TemplateEditorDialog
        open={!!wordingTemplate}
        templateKey={wordingKey ?? ''}
        templateName={wordingTemplate?.name}
        onClose={() => setWordingStepId(null)}
        onSave={() => {
          queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reminderSteps() });
          queryClient.invalidateQueries({ queryKey: adminSettingsKeys.invoice() });
          queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
          setWordingStepId(null);
        }}
      />
    </Card>
  );
}
